import { initializePaddle, type Paddle, type PaddleEventData } from '@paddle/paddle-js'

const PADDLE_CLIENT_TOKEN = import.meta.env.VITE_PADDLE_CLIENT_TOKEN || ''
const PADDLE_ENVIRONMENT = (import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox') as
  | 'sandbox'
  | 'production'

let paddlePromise: Promise<Paddle | undefined> | null = null
// Paddle takes one global event callback at init, so the per-checkout close
// handler lives here. Cleared on completion so a successful payment never
// triggers the "closed without paying" path.
let onClosed: (() => void) | null = null

function handleEvent(event: PaddleEventData) {
  if (event.name === 'checkout.completed') {
    onClosed = null
  } else if (event.name === 'checkout.closed') {
    const cb = onClosed
    onClosed = null
    cb?.()
  }
}

function getPaddle(): Promise<Paddle | undefined> {
  if (!paddlePromise) {
    const attempt = initializePaddle({
      environment: PADDLE_ENVIRONMENT,
      token: PADDLE_CLIENT_TOKEN,
      eventCallback: handleEvent,
    })
    paddlePromise = attempt
    // A blocked or failed script load resolves undefined or rejects; forget it
    // so the next checkout click retries instead of failing until reload.
    attempt.then(
      (p) => { if (!p && paddlePromise === attempt) paddlePromise = null },
      () => { if (paddlePromise === attempt) paddlePromise = null },
    )
  }
  return paddlePromise
}

export type CheckoutOptions = {
  /** Called when the buyer closes the overlay without completing payment. */
  onClose?: () => void
}

export async function openPaddleCheckout(transactionId: string, successUrl: string, opts: CheckoutOptions = {}) {
  const paddle = await getPaddle()
  if (!paddle) {
    throw new Error('Paddle.js failed to initialize')
  }
  onClosed = opts.onClose ?? null
  paddle.Checkout.open({
    transactionId,
    // The server attaches the Paddle customer, so the email is prefilled;
    // keep the buyer on that account email.
    settings: { successUrl, allowLogout: false },
  })
}

export function isPaddleConfigured(): boolean {
  return !!PADDLE_CLIENT_TOKEN
}
